/**
 * 인증 상태 관리 스토어 (Zustand)
 * - 로그인 상태, 사용자 정보 관리
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as authApi from '../api/auth.api';
import i18n from '../i18n';

interface AuthState {
  isAuthenticated: boolean;
  user: authApi.User | null;
  accessToken: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  setUser: (user: authApi.User) => void;
  setToken: (accessToken: string, refreshToken?: string) => void;
  clearToken: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      user: null,
      accessToken: null,

      /**
       * 로그인
       */
      login: async (email: string, password: string) => {
        try {
          const response = await authApi.login({ email, password });

          // 토큰 저장
          localStorage.setItem('access_token', response.access_token);

          set({
            isAuthenticated: true,
            user: response.user,
            accessToken: response.access_token,
          });

          if (response.user.preferences?.language) {
            i18n.changeLanguage(response.user.preferences!.language!);
          }

        } catch (error) {
          console.error('❌ 로그인 실패:', error);
          throw error;
        }
      },

      /**
       * 회원가입
       */
      register: async (email: string, password: string, name: string) => {
        try {
          const response = await authApi.register({ email, password, name });

          // 토큰 저장
          localStorage.setItem('access_token', response.access_token);

          set({
            isAuthenticated: true,
            user: response.user,
            accessToken: response.access_token,
          });

          if (response.user.preferences?.language) {
            i18n.changeLanguage(response.user.preferences!.language!);
          }

        } catch (error) {
          console.error('❌ 회원가입 실패:', error);
          throw error;
        }
      },

      /**
       * 로그아웃
       */
      logout: () => {
        // 토큰 제거
        localStorage.removeItem('access_token');
        
        set({
          isAuthenticated: false,
          user: null,
          accessToken: null,
        });
      },

      /**
       * 사용자 정보 새로고침
       */
      refreshUser: async () => {
        try {
          const user = await authApi.getProfile();
          set({ user });
        } catch (error) {
          console.error('❌ 사용자 정보 새로고침 실패:', error);
          // 인증 실패 시 로그아웃
          get().logout();
        }
      },

      /**
       * 사용자 정보 설정
       */
      setUser: (user: authApi.User) => {
        set({ user });
      },

      /**
       * OAuth 콜백 등 외부 토큰 설정
       */
      setToken: (accessToken: string, refreshToken?: string) => {
        localStorage.setItem('access_token', accessToken);
        if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
        set({ accessToken, isAuthenticated: true });
      },

      /**
       * 토큰 및 인증 상태 초기화
       */
      clearToken: () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        set({ accessToken: null, isAuthenticated: false, user: null });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        accessToken: state.accessToken,
      }),
    }
  )
);

